import React, { useState, useEffect } from 'react';
import Profile from "../images/profile.png";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { getToken } from '../utils/getToken';

const baseURL = process.env.REACT_APP_BASE_URL || 'https://api.quickcare.ng/admin';

const PersonalInfo = () => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [profile, setProfile] = useState({
        full_name: '',
        dateOfBirth: '',
        gender: '',
        homeAddress: '',
        profilePhoto: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const handleImageChange = (event) => {
        const imageFile = event.target.files[0];
        if (imageFile) {
            const imageUrl = URL.createObjectURL(imageFile);
            setSelectedImage(imageUrl);
        }
    };

    useEffect(() => {
        const fetchProfileData = async () => {
            const token = getToken();
            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow"
            };

            try {
                const response = await fetch(`${baseURL}/profile`, requestOptions);
                const result = await response.json();

                if (response.ok) {
                    const data = result.data;
                    setProfile({
                        full_name: data.full_name || 'N/A',
                        dateOfBirth: data.dateOfBirth || 'N/A',
                        gender: data.gender || 'N/A',
                        homeAddress: data.homeAddress || 'N/A',
                        profilePhoto: data.photo || Profile,
                        role: data.role || 'N/A',
                        email: data.email || 'N/A',
                        phone_number: data.phone_number || 'N/A',
                        active: data.active ? 'Active' : 'Inactive'
                    });
                } else {
                    setError(result.message || 'Failed to fetch profile data');
                }
            } catch (error) {
                console.error("Fetch error:", error);
                setError('An error occurred');
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, []);

    if (loading) {
        return (
            <div className="w-full bg-white rounded-2xl p-8">
                <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#069125]"></div>
                    <p className="text-sm text-gray-500 mt-4">Loading profile...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full bg-white rounded-2xl p-8">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                    <span className="text-4xl mb-3">⚠️</span>
                    <h3 className="text-lg font-semibold text-gray-700">Error Loading Profile</h3>
                    <p className="text-sm text-gray-500 mt-1">{error}</p>
                </div>
            </div>
        );
    }

    // Get status color
    const getStatusColor = (status) => {
        return status === 'Active' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
    };

    return (
        <div className='w-full bg-white rounded-2xl border border-gray-100 overflow-hidden'>
            {/* Profile Photo Section */}
            <div className='p-4 lg:p-6 border-b border-gray-100'>
                <div className='flex flex-col sm:flex-row items-start sm:items-center gap-4'>
                    <div className='flex-1'>
                        <h3 className='text-sm font-semibold text-gray-800'>Profile Photo</h3>
                        <p className='text-xs text-gray-400 mt-1'>This image will be displayed on your profile</p>
                        <label 
                            htmlFor="image-upload" 
                            className="inline-flex items-center gap-2 mt-3 px-4 py-2 border-2 border-[#069125] text-[#069125] font-medium rounded-xl cursor-pointer hover:bg-[#069125] hover:text-white transition-all duration-200 text-sm"
                        >
                            <ImageOutlinedIcon className='text-sm' />
                            <span>Change Photo</span>
                        </label>
                        <input 
                            id="image-upload" 
                            type="file" 
                            accept="image/*" 
                            onChange={handleImageChange} 
                            style={{ display: 'none' }} 
                        />
                    </div>
                    <div className='w-20 h-20 rounded-full overflow-hidden border-4 border-[#069125]/20 flex-shrink-0'>
                        <img 
                            src={selectedImage || profile.profilePhoto} 
                            alt="Profile" 
                            className='w-full h-full object-cover'
                        />
                    </div>
                </div>
            </div>

            {/* Personal Information */}
            <div className='p-4 lg:p-6'>
                <h3 className='text-sm font-semibold text-gray-800 mb-4'>Personal Information</h3>
                
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    <div className='bg-gray-50 rounded-xl p-3'>
                        <p className='text-xs text-gray-400 font-medium'>Full Name</p>
                        <p className='text-sm font-semibold text-gray-800 mt-1'>{profile.full_name}</p>
                    </div>

                    <div className='bg-gray-50 rounded-xl p-3'>
                        <p className='text-xs text-gray-400 font-medium'>Role</p>
                        <p className='text-sm font-semibold text-gray-800 mt-1'>
                            <span className='inline-block px-2.5 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs'>
                                {profile.role}
                            </span>
                        </p>
                    </div>

                    <div className='bg-gray-50 rounded-xl p-3'>
                        <p className='text-xs text-gray-400 font-medium'>Email</p>
                        <p className='text-sm font-semibold text-gray-800 mt-1 break-all'>{profile.email}</p>
                    </div>

                    <div className='bg-gray-50 rounded-xl p-3'>
                        <p className='text-xs text-gray-400 font-medium'>Phone Number</p>
                        <p className='text-sm font-semibold text-gray-800 mt-1'>{profile.phone_number}</p>
                    </div>

                    <div className='bg-gray-50 rounded-xl p-3'>
                        <p className='text-xs text-gray-400 font-medium'>Status</p>
                        <p className='mt-1'>
                            <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(profile.active)}`}>
                                {profile.active}
                            </span>
                        </p>
                    </div>

                    {profile.dateOfBirth !== 'N/A' && (
                        <div className='bg-gray-50 rounded-xl p-3'>
                            <p className='text-xs text-gray-400 font-medium'>Date of Birth</p>
                            <p className='text-sm font-semibold text-gray-800 mt-1'>{profile.dateOfBirth}</p>
                        </div>
                    )}

                    {profile.gender !== 'N/A' && (
                        <div className='bg-gray-50 rounded-xl p-3'>
                            <p className='text-xs text-gray-400 font-medium'>Gender</p>
                            <p className='text-sm font-semibold text-gray-800 mt-1'>{profile.gender}</p>
                        </div>
                    )}

                    {profile.homeAddress !== 'N/A' && (
                        <div className='bg-gray-50 rounded-xl p-3'>
                            <p className='text-xs text-gray-400 font-medium'>Home Address</p>
                            <p className='text-sm font-semibold text-gray-800 mt-1'>{profile.homeAddress}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PersonalInfo;